import { Component } from 'react'
import PropTypes from 'prop-types'
import $ from 'jquery'
import { withTranslation } from 'react-i18next'
import { XIcon, CalendarCheckIcon } from '@phosphor-icons/react'
import { Typography } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import { useHref } from 'react-router-dom'

import {
  getTrainingType,
  formatStartAndEndDates,
  getTimezoneShortName,
  getBuildingTimezoneFromEvent,
  getDates,
  getMultidayTime,
  getTimeString,
  canCancelReservation,
} from '../../helpers/helpers'
import { useTranslation } from 'react-i18next'
import { EMPTY_VALUE } from '@/constants/ui'
import ReservationNotesModal from '../Reservations/ReservationNotesModal/ReservationNotesModal'

const MINIMUM_CAPACITY_VALUE = 1

const DetailsButton = ({ eventId }) => {
  const { t } = useTranslation(['calendar'])
  const href = useHref(`/reservations/${eventId}`)

  return (
    <Button use="secondary" onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}>
      {t('calendar:amenityPopup.reservationDetails')}
    </Button>
  )
}

class CalendarAmenityPopup extends Component {
  constructor(props) {
    super(props)
    this.state = {
      arrowPosition: '',
      notesReservation: null,
    }
  }

  componentWillReceiveProps(nextProps) {
    const { open, target } = nextProps
    this.removeEventActiveClass()
    if (open) {
      this.calculatePopupPosition(target)
      this.setEventActiveClass(target)
    }
  }

  calculatePopupPosition(target) {
    const popup = $('.ctp-event-popup')
    const arrow = $('.ctp-event-popup__arrow')
    const container = $('.ctp-calendar')

    const popupHeight = popup.outerHeight()
    const popupWidth = popup.outerWidth()
    const containerOffset = container.offset()
    const containerWidth = container.width()
    const scrollTop = container.scrollTop()
    const scrollLeft = container.scrollLeft()

    const targetOffset = target.offset()
    const targetHeight = target.outerHeight()
    const targetWidth = target.outerWidth()

    const targetTopInContent = targetOffset.top - containerOffset.top + scrollTop
    const targetLeftInContent = targetOffset.left - containerOffset.left + scrollLeft
    const targetCenterYInContent = targetTopInContent + targetHeight / 2

    const targetRightInContent = targetLeftInContent + targetWidth
    const spaceOnRight = containerWidth - (targetRightInContent - scrollLeft)

    let popupLeft
    if (spaceOnRight >= popupWidth) {
      popupLeft = targetRightInContent
      this.setState({ arrowPosition: 'left' })
    } else {
      popupLeft = targetLeftInContent - popupWidth
      this.setState({ arrowPosition: 'right' })
    }

    let popupTop = targetCenterYInContent - popupHeight / 2

    const minTop = scrollTop
    const maxTop = scrollTop + container.height() - popupHeight
    popupTop = Math.max(minTop, Math.min(maxTop, popupTop))

    popup.css({ left: popupLeft, top: popupTop })

    const arrowTop = targetCenterYInContent - popupTop - 8
    arrow.css({ top: arrowTop, bottom: 'auto' })
  }

  setEventActiveClass(target) {
    target.addClass('ctp-event--active')
  }

  removeEventActiveClass() {
    $('.ctp-event').removeClass('ctp-event--active')
  }

  render() {
    const {
      buildingData,
      userData,
      userRole,
      event,
      open,
      onClose,
      openAttendingPopup,
      onCancelReservationEvent,
      t,
    } = this.props
    const trainingType = getTrainingType(event)
    const timezone = getBuildingTimezoneFromEvent(event)

    let eventType = event.name
    const locationName = event.locationName

    let [start, end] = event.isMultiday
      ? getMultidayTime(event, timezone)
      : formatStartAndEndDates(trainingType, event, timezone)

    const user = event.client || event.guest
    const timezoneSuffix = timezone ? ` ${getTimezoneShortName(timezone)}` : ''
    // Resident/price/contact details only apply to amenity reservations, not appointments,
    // reminders or sign-up-link events
    const showAmenityDetails =
      !['appointment', 'reminder'].includes(trainingType) && !event.signUpLink

    const dateInfo = event.isMultiday
      ? getDates(event, timezone)
      : `${start} - ${end}${timezoneSuffix}`

    const infoItems = [
      {
        label: t('calendar:amenityPopup.amenityType'),
        value: eventType,
      },
      {
        hidden: !locationName,
        label: t('calendar:amenityPopup.space'),
        value: locationName,
      },
      {
        hidden: !showAmenityDetails || event.endDate || event.capacity <= MINIMUM_CAPACITY_VALUE,
        label: t('calendar:amenityPopup.capacity'),
        value: event.capacity,
      },
      {
        hidden: !showAmenityDetails,
        label: t('calendar:amenityPopup.resident'),
        value: user
          ? `${user.firstName} ${user.lastName}${user.unit ? ` #${user.unit}` : ''}`
          : EMPTY_VALUE,
      },
      {
        hidden: !showAmenityDetails || !event.isMultiday,
        label: t('calendar:amenityPopup.checkInTime'),
        value: `${start}${timezoneSuffix}`,
      },
      {
        hidden: !showAmenityDetails || !event.isMultiday,
        label: t('calendar:amenityPopup.checkOutTime'),
        value: `${end}${timezoneSuffix}`,
      },
      {
        hidden: !showAmenityDetails,
        label: t('calendar:amenityPopup.price'),
        value: `$${event.chargeAmount}`,
      },
      {
        hidden: !showAmenityDetails || !user,
        label: t('calendar:amenityPopup.email'),
        value: user?.email,
      },
      {
        hidden: !showAmenityDetails || !user,
        label: t('calendar:amenityPopup.phone'),
        value: user?.phone,
      },
      {
        hidden: !showAmenityDetails || event.endDate,
        label: t('calendar:amenityPopup.duration'),
        value: getTimeString(event.duration),
      },
    ]

    return (
      <div
        className={`ctp-event-popup calendar-time-block-reservation-popup ${open ? 'ctp-event-popup--active' : ''} ${
          this.state.arrowPosition === 'right' ? 'ctp-event-popup--pos-right' : ''
        }`}
      >
        <span className="ctp-event-popup__arrow" />
        <header className="calendar-time-block-reservation-popup__header reservation">
          <div className="title">
            <div className="reservation-item__icon reservation">
              <CalendarCheckIcon />
            </div>

            <Typography as="span" size="heading-tiny" color="black800">
              {event.title}
            </Typography>

            <div className="close-button" onClick={onClose}>
              <XIcon />
            </div>
          </div>
          <div className="actions">
            {canCancelReservation(userRole, event.status) && (
              <Button
                use="secondary"
                onClick={(e) => {
                  onCancelReservationEvent(e, this.props.event)
                }}
              >
                <XIcon />
                {t('common:actions.cancel')}
              </Button>
            )}
            {['Trainer', 'Manager', 'Administrator', 'PropertyManager', 'ShiftManager'].includes(
              userRole,
            ) &&
              event.taken > 0 && (
                <Button use="secondary" onClick={(e) => openAttendingPopup(e, 'attending', event)}>
                  {t('calendar:agenda.checkIn')}
                </Button>
              )}
            {userRole === 'PropertyManager' && event.taken > 0 && (
              <Button use="secondary" onClick={(e) => openAttendingPopup(e, 'attending', event)}>
                {t('calendar:amenityPopup.manageRsvp')}
              </Button>
            )}
            <DetailsButton eventId={event.id} />
          </div>
        </header>

        <div className="ctp-event-popup__inner">
          <Typography size="heading-tiny">{dateInfo}</Typography>

          <div className="time-block-info-container">
            {infoItems.map(({ label, value, hidden }) => {
              if (hidden) {
                return null
              }

              return (
                <div key={label} className="time-block-info">
                  <Typography size="text-medium" color="black500" weight="medium">
                    {label}
                  </Typography>

                  {typeof value === 'string' || typeof value === 'number' ? (
                    <Typography color="black800" size="text-medium" weight="medium">
                      {value}
                    </Typography>
                  ) : (
                    value
                  )}
                </div>
              )
            })}
          </div>
        </div>
        {this.state.notesReservation && (
          <ReservationNotesModal
            onClose={() => this.setState({ ...this.state, notesReservation: null })}
            reservationId={this.state.notesReservation.id}
            timezone={buildingData.timezone}
            userData={userData}
            isTimeBlock={this.state.notesReservation.status === 'blocked'}
          />
        )}
      </div>
    )
  }
}

CalendarAmenityPopup.propTypes = {
  buildingData: PropTypes.object,
  userData: PropTypes.object,
  event: PropTypes.object.isRequired,
  userRole: PropTypes.string.isRequired,
  onEditEvent: PropTypes.func,
  openAttendingPopup: PropTypes.func,
  onCancelReservationEvent: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  target: PropTypes.object,
  t: PropTypes.func,
}

export default withTranslation(['calendar'])(CalendarAmenityPopup)
